import { STEP_STATES } from 'helpers/enums';
const STEP_STATUS = { pending: 0, done: 1, skipped: -1 };

const defaultStatus = (t) => ({
  label: t('buttonsActions.add'),
  actionEnabled: true,
  state: STEP_STATES.notStarted
});

const isStepDone = (key, steps) =>
  [STEP_STATES.done, STEP_STATES.completed].includes(
    steps?.find((step) => step.key === key)?.status?.state || STEP_STATES.notStarted
  );

const isStepIncomplete = ({ step, steps, app }) => {
  // First step has no previous step
  if (!step) return false;

  // Use cached result if available
  if ([true, false].includes(step?.done)) {
    return !step?.done;
  }

  // Use custom isDone function if provided
  if (step?.isDone) {
    return !step.isDone({ app });
  }

  // Use default completion check
  return !isStepDone(step.key, steps);
};

const previousStepIncomplete = ({ app, steps, step }) => {
  // Find the last non-hidden step before this one
  const currentStepIndex = steps.findIndex((s) => s.key === step.key);
  const prevStep = steps
    .slice(0, currentStepIndex)
    .reverse()
    .filter((s) => !s?.status?.hidden)[0];

  return isStepIncomplete({ step: prevStep, steps, app });
};

const previousStepsIncomplete = ({ app, steps, step }) => {
  // Find all non-hidden steps before this one
  const currentStepIndex = steps.findIndex((s) => s.key === step.key);
  const prevSteps = steps.slice(0, currentStepIndex).filter((s) => !s?.status?.hidden);

  //iterate through them and see if we have any incomplete
  return prevSteps.some((s) => {
    const isIncomplete = isStepIncomplete({ step: s, steps, app });
    return isIncomplete;
  });
};

const applicationStepStatus = {
  identity: ({ appStep, t }) => {
    const status = { ...defaultStatus(t) };
    if ([STEP_STATUS.done, STEP_STATUS.skipped].includes(appStep?.status)) {
      status.state = STEP_STATES.done;
      status.label = t('buttonsActions.done');
      status.actionEnabled = false;
    }
    return status;
  },
  ['org-terms']: ({ appStep, t, location }) => {
    const status = { ...defaultStatus(t) };
    if (!location?.applicationConfig?.applicationConditionsTemplateId) {
      status.label = t('buttonsActions.skipped');
      status.state = STEP_STATES.done;
      status.icon = 'fa-duotone fa-arrow-right-long-to-line';
      return status;
    }

    if ([STEP_STATUS.done, STEP_STATUS.skipped].includes(appStep?.status)) {
      status.state = STEP_STATES.done;
      status.label = t('buttonsActions.done');
      status.actionEnabled = false;
    }
    return status;
  },
  appFee: ({ appStep, t }) => {
    const status = { ...defaultStatus(t), label: t('buttonsActions.pay') };
    if ([STEP_STATUS.done, STEP_STATUS.skipped].includes(appStep?.status)) {
      status.label = t('buttonsActions.paid');
      status.state = STEP_STATES.done;
      status.actionEnabled = false;
    }
    return status;
  },
  backgroundCheck: ({ appStep, t }) => {
    const status = { ...defaultStatus(t) };
    if ([STEP_STATUS.done, STEP_STATUS.skipped].includes(appStep?.status)) {
      status.label = t('buttonsActions.done');
      status.state = STEP_STATES.done;
      //   status.actionEnabled = false;
    }
    return status;
  },
  incomeVerification: ({ application, appStep, t }) => {
    const status = { ...defaultStatus(t) };
    if (
      !application?.verifastBankComplete &&
      ([STEP_STATUS.skipped].includes(appStep?.status) ||
        application?.autoIncomeSkipDetails?.skipped)
    ) {
      status.label = t('buttonsActions.skipped');
      status.state = STEP_STATES.done;
      status.icon = 'fa-duotone fa-arrow-right-long-to-line';
    } else if (
      application?.verifastBankComplete &&
      [STEP_STATUS.skipped, STEP_STATUS.done].includes(appStep?.status)
    ) {
      status.label = t('buttonsActions.done');
      status.state = STEP_STATES.done;
      status.actionEnabled = false;
    }
    return status;
  },
  income: ({ appStep, t }) => {
    const status = { ...defaultStatus(t) };
    if ([STEP_STATUS.skipped].includes(appStep?.status)) {
      status.label = t('buttonsActions.skipped');
      status.state = STEP_STATES.done;
      status.icon = 'fa-duotone fa-arrow-right-long-to-line';
    }

    if ([STEP_STATUS.done].includes(appStep?.status)) {
      status.label = t('buttonsActions.update');
      status.state = STEP_STATES.completed;
    }
    return status;
  },
  submit: ({ appStep, t }) => {
    const status = { ...defaultStatus(t) };
    if ([STEP_STATUS.done, STEP_STATUS.skipped].includes(appStep?.status)) {
      status.state = STEP_STATES.done;
      status.label = t('buttonsActions.done');
      status.actionEnabled = false;
    }
    return status;
  }
};

const getApplicationStepStatus = ({ key, application, location, appStep, step, t }) => {
  // Use custom status handler if available
  if (applicationStepStatus[key]) {
    return applicationStepStatus[key]({ application, location, appStep, step, t });
  }

  // Default status logic
  const status = { ...defaultStatus(t) };
  if ([STEP_STATUS.done, STEP_STATUS.skipped].includes(appStep?.status)) {
    return {
      ...status,
      label: t('buttonsActions.update'),
      state: STEP_STATES.completed
    };
  }

  return status;
};

const encodeStep = ({ step, application, steps }) => {
  // Determine if step is done
  step.done = step.isDone ? step.isDone({ app: application }) : isStepDone(step.key, steps);

  // Set visibility and locking
  step.status.hidden = !!step.hidden;
  if (step.requireAllPrevious) {
    step.status.locked = previousStepsIncomplete({ app: application, steps, step });
  } else {
    step.status.locked = previousStepIncomplete({ app: application, steps, step });
  }

  // Apply custom locking logic if provided
  if (step?.isLocked) {
    step.status.locked = step.status.locked || step.isLocked({ app: application });
  }

  return step;
};

export default ({ stepOrder, stepMap, application, location, t }) => {
  // map the order into steps with status
  const steps = stepOrder.map((stepKey) => {
    const baseStep = stepMap[stepKey];
    const appStep = application?.steps?.find((st) => st.key === stepKey);
    const stepStatus =
      getApplicationStepStatus({
        key: stepKey,
        application,
        location,
        appStep,
        step: baseStep,
        t
      }) || {};
    return { ...baseStep, status: stepStatus };
  });

  return steps.map((step) => encodeStep({ step, application, steps }));
};
